import React, { useEffect, useState } from 'react';
import { dogProfiles } from '../../data/dogProfiles';
import DogImageGallery from '../ImageGallery';
import { trackEvent } from '../../utils/matomoEvents';
import { photoPosition } from '../../utils/photoPosition';

const FALLBACK_IMAGE =
  'https://images.unsplash.com/photo-1583337130417-3346a1be7dee?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80';

/**
 * Shared profile page for every dog under /our-gators/:id.
 * All content comes from dogProfiles, including dogPage_objectPosition,
 * which controls how the profile photo is cropped on this page.
 */
export default function DogProfilePage({ dogId }) {
  const [galleryImages, setGalleryImages] = useState([]);

  const profile = dogProfiles[dogId];
  const galleryPath = profile?.gallery;
  // Matomo labels predate this component; keep the original label where it
  // differed from the display name so historical reports stay continuous.
  const analyticsName = profile ? profile.analyticsName || profile.name : null;

  // Track profile view on mount
  useEffect(() => {
    if (!analyticsName) return;
    trackEvent('DogProfile', 'ViewProfile', analyticsName);
  }, [analyticsName]);

  useEffect(() => {
    if (!galleryPath) return undefined;

    let cancelled = false;
    fetch(`${galleryPath}/index.json`)
      .then(response => response.json())
      .then(data => {
        if (cancelled) return;
        const images = data.photos.map(photo => `${galleryPath}/${photo}`);
        setGalleryImages(images);
        trackEvent('DogProfile', 'GalleryLoad', analyticsName, images.length);
      })
      .catch(error => console.error('Error fetching gallery images:', error));

    return () => {
      cancelled = true;
    };
  }, [galleryPath, analyticsName]);

  if (!profile) return null;

  return (
    <div className="dog-page">
      <section className="bio-section">
        <h2>{profile.name}</h2>
        <div className="bio-content">
          <img
            src={profile.image}
            alt={profile.name}
            style={photoPosition(
              profile.dogPage_objectPosition,
              profile.dogPage_mobile_objectPosition
            )}
            onError={(e) => {
              e.target.src = FALLBACK_IMAGE;
            }}
          />
          <div className="bio-text">
            <p>{profile.long_bio}</p>
            <ul className="dog-details">
              <li><strong>Status:</strong> {profile.status}</li>
              <li><strong>Color:</strong> {profile.color}</li>
              <li><strong>Weight:</strong> {profile.weight}</li>
              <li><strong>DNA:</strong> {profile.dna}</li>
              <li><strong>Temperament:</strong> {profile.temperament}</li>
            </ul>
          </div>
        </div>
      </section>

      <section className="gallery-section">
        <h2>Photo Gallery</h2>
        <DogImageGallery images={galleryImages} />
      </section>

      <style jsx>{`
        .dog-page {
          padding: 2rem;
          max-width: 1200px;
          margin: 0 auto;
        }
        .bio-section {
          margin-bottom: 3rem;
        }
        .bio-section h2 {
          font-size: 2.5rem;
          color: var(--text);
          margin-bottom: 1.5rem;
        }
        .bio-content {
          display: grid;
          grid-template-columns: 300px 1fr;
          gap: 2rem;
          align-items: start;
        }
        .bio-content img {
          width: 100%;
          height: 300px;
          object-fit: cover;
          object-position: var(--photo-pos, center);
          border-radius: 10px;
          box-shadow: 0 4px 8px var(--shadow);
        }
        .bio-text {
          font-size: 1.1rem;
          line-height: 1.6;
          color: var(--text-muted);
        }
        .dog-details {
          list-style: none;
          padding: 0;
          margin-top: 1.5rem;
        }
        .dog-details li {
          margin: 0.5rem 0;
          font-size: 1rem;
        }

        .gallery-section {
          max-width: 500px;
          margin: 2rem auto;
          padding: 2rem;
          background-color: var(--surface-alt);
          border-radius: 15px; /* Add rounded corners */
        }

        .gallery-section h2 {
          text-align: center;
          font-size: 2rem;
          color: var(--text);
          margin-bottom: 2rem;
        }

        .gallery-section img {
          object-fit: cover;
          width: 100%;
          height: 540px;
        }

        @media (max-width: 768px) {
          .bio-content {
            grid-template-columns: 1fr;
          }
          .bio-content img {
            height: 250px;
            /* dogPage_mobile_objectPosition, when a dog has one. */
            object-position: var(--photo-pos-mobile, var(--photo-pos, center));
          }
        }
      `}</style>
    </div>
  );
}
